import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import notificationError from '@/utils/functions/notificationError';
import {
  calculateTaxes,
  invoiceAdditionalData,
  invoiceCreateNFc,
  invoiceCreateNFe,
  invoiceCreateNFs,
  invoiceDetail,
  invoiceEditNFc,
  invoiceEditNFe,
  invoiceEditNFs,
  invoiceList,
  invoiceRemove,
} from './invoices.services';
import { InvoiceListParams } from './invoices.types';
import { notification } from 'antd';
import { DEFAULT_ROWS } from '..';
import { InvoiceErrors } from './invoices.errors';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function useInvoiceList(params?: InvoiceListParams) {
  return useInfiniteQuery(
    ['invoices', { ...params }],
    ({ pageParam: nextToken }) => {
      const queryParams = {
        nextToken,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return invoiceList(queryParams);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useInvoiceDetail(id?: number) {
  return useQuery(['invoiceDetail', id], () => invoiceDetail(id), {
    enabled: !!id,
    onError(error) {
      notificationError(error);
    },
  });
}

export function useInvoiceAdditionalData() {
  const queryClient = useQueryClient();

  return useMutation(invoiceAdditionalData, {
    async onSuccess(_, data) {
      notification.success({
        message: 'Dados adicionais da nota fiscal atualizados com sucesso!',
      });
      await queryClient.invalidateQueries(['invoiceDetail', data.id]);
      await queryClient.invalidateQueries(['invoices']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useInvoiceRemove() {
  const queryClient = useQueryClient();

  return useMutation(invoiceRemove, {
    async onSuccess() {
      notification.success({
        message: 'Nota fiscal removida com sucesso!',
      });
      await queryClient.invalidateQueries(['invoices']);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

// NFC

export function useInvoiceCreateNFc() {
  const queryClient = useQueryClient();

  return useMutation(invoiceCreateNFc, {
    async onSuccess() {
      notification.success({
        message: 'NFC-e cadastrado com sucesso!',
      });
      await queryClient.invalidateQueries(['invoices']);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) => errors.map((err) => InvoiceErrors[err] || err),
      });
    },
  });
}

export function useInvoiceEditNFc() {
  const queryClient = useQueryClient();

  return useMutation(invoiceEditNFc, {
    async onSuccess(data) {
      notification.success({
        message: 'NFC-e editado com sucesso!',
      });
      await queryClient.invalidateQueries(['invoice', data.id]);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

// NFE

export function useInvoiceCreateNFe() {
  const queryClient = useQueryClient();

  return useMutation(invoiceCreateNFe, {
    async onSuccess() {
      notification.success({
        message: 'NF-e cadastrado com sucesso!',
      });
      await queryClient.invalidateQueries(['invoices']);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) => errors.map((err) => InvoiceErrors[err] || err),
      });
    },
  });
}

export function useInvoiceEditNFe() {
  const queryClient = useQueryClient();

  return useMutation(invoiceEditNFe, {
    async onSuccess(data) {
      notification.success({
        message: 'NF-e editado com sucesso!',
      });
      await queryClient.invalidateQueries(['invoice', data.id]);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

// NFS

export function useInvoiceCreateNFs() {
  const queryClient = useQueryClient();

  return useMutation(invoiceCreateNFs, {
    async onSuccess() {
      notification.success({
        message: 'NFS-e cadastrado com sucesso!',
      });
      await queryClient.invalidateQueries(['invoices']);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) => errors.map((err) => InvoiceErrors[err] || err),
      });
    },
  });
}

export function useInvoiceEditNFs() {
  const queryClient = useQueryClient();

  return useMutation(invoiceEditNFs, {
    async onSuccess(data) {
      notification.success({
        message: 'NFS-e editado com sucesso!',
      });

      await queryClient.invalidateQueries(['invoice', data.id]);
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useCalculateTaxes() {
  const queryChanel = useQueryChanel();

  return useMutation(calculateTaxes, {
    async onSuccess(_, id) {
      notification.success({
        message: 'Sucesso',
        description: 'Tributos da nota calculados com sucesso!',
      });

      queryChanel({
        invalidateQueries: [
          ['invoiceDetail', id],
          ['invoiceItems', { invoiceId: id }],
          ['transport'],
        ],
        resetQueries: [
          ['invoiceDetail', id],
          ['invoiceItems', { invoiceId: id }],
        ],
      });
    },

    onError(error) {
      notificationError(error, {
        message: 'Calcular tributos',
        translate: (errors) => errors.map((err) => InvoiceErrors[err] || err),
      });
    },
  });
}
